import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { axisLabels, formatBrier, formatDate, formatPercent } from './format.js';
import CalibrationChart from './charts/CalibrationChart.jsx';
import BrierOverTimeChart from './charts/BrierOverTimeChart.jsx';
import CategoryBrierChart from './charts/CategoryBrierChart.jsx';
import ConfidenceChart from './charts/ConfidenceChart.jsx';

// The track record across every set one admin code owns — the view the spreadsheet could never
// give, because each year lived on its own tab.
//
// Only rendered once a code has revealed some sets. That isn't a decorative gate: these charts
// pool somebody's private predictions across years, and the code is the only thing that says the
// years belong together.

// Sets are laid out oldest first. Undated sets keep their stored position instead of being sorted
// to an arbitrary end — sort_order is what the list page orders by, and it is usually right.
function chronological(sets) {
  return sets
    .map((set, index) => ({ set, index }))
    .sort((a, b) => {
      const dateA = a.set.predictionsMadeOn;
      const dateB = b.set.predictionsMadeOn;
      if (dateA && dateB && dateA !== dateB) return dateA < dateB ? -1 : 1;
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      return a.index - b.index;
    })
    .map(entry => entry.set);
}

function Stat({ label, value, hint }) {
  return (
    <div className="pred-stat">
      <div className="pred-stat-value">{value}</div>
      <div className="pred-stat-label">{label}</div>
      <div className="pred-stat-hint">{hint || ' '}</div>
    </div>
  );
}

export default function CrossSetPanel({ code }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]); // empty = every category, not none
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const loadedOnce = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // A filter change refetches, but the old charts stay on screen while it does. Swapping them
    // for a spinner on every checkbox would make the panel flicker its whole height.
    setStatus(loadedOnce.current ? 'refreshing' : 'loading');
    api
      .aggregate(code, selected)
      .then(next => {
        if (cancelled) return;
        loadedOnce.current = true;
        setData(next);
        setError('');
        setStatus('ready');
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [code, selected]);

  const points = useMemo(() => {
    if (!data) return [];
    const graded = chronological(data.bySet).filter(set => set.summary.scored > 0);
    // Labels come from the set names, shortened against each other — the axis positions carry the
    // timing, so the label's job is only to say which set. A date-derived label can't do that job:
    // weekly sets would all print the same year.
    const labels = axisLabels(graded.map(set => set.name));
    return graded.map((set, i) => ({
      key: set.slug,
      label: labels[i],
      fullLabel: set.name,
      madeOn: set.predictionsMadeOn,
      writtenOn: formatDate(set.predictionsMadeOn),
      evaluatedOn: formatDate(set.evaluatedOn),
      scored: set.summary.scored,
      brier: set.summary.brier,
      baseline: set.summary.baselineBrier,
      confidence: set.summary.meanConfidence,
      accuracy: set.summary.accuracy
    }));
  }, [data]);

  const categoryRows = useMemo(
    () =>
      (data ? data.byCategory : []).map(row => ({
        name: row.name,
        brier: row.summary.brier,
        baseline: row.summary.baselineBrier,
        scored: row.summary.scored,
        happened: row.summary.happened,
        yesRate: row.summary.yesRate
      })),
    [data]
  );

  if (status === 'loading') return <p className="pred-loading">Loading combined stats…</p>;
  if (!data) return error ? <p className="pred-error">{error}</p> : null;

  const { summary, calibration } = data.combined;
  const graded = summary.scored > 0;
  const gap =
    summary.meanConfidence !== null && summary.accuracy !== null
      ? summary.meanConfidence - summary.accuracy
      : null;
  const setsWithGrades = data.bySet.filter(set => set.summary.scored > 0).length;

  function toggleCategory(name) {
    setSelected(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]));
  }

  return (
    <section className={`pred-cross ${status === 'refreshing' ? 'is-refreshing' : ''}`}>
      <div className="pred-cross-head">
        <h2>Across all {data.bySet.length} sets</h2>
        <p className="pred-lede">
          The base-rate baseline is the Brier score you'd get by ignoring the questions and giving
          every one your overall hit rate.
        </p>
      </div>

      <div className="pred-cat-filter" role="group" aria-label="Filter by category">
        <button
          type="button"
          className={`pred-chip ${selected.length ? '' : 'is-on'}`}
          aria-pressed={selected.length === 0}
          onClick={() => setSelected([])}
        >
          All categories
        </button>
        {data.categories.map(category => {
          const on = selected.includes(category.name);
          return (
            <button
              key={category.name}
              type="button"
              className={`pred-chip ${on ? 'is-on' : ''}`}
              aria-pressed={on}
              onClick={() => toggleCategory(category.name)}
            >
              {category.name}
              <span className="pred-chip-count">{category.total}</span>
            </button>
          );
        })}
      </div>

      {error && <p className="pred-error">{error}</p>}

      <div className="pred-stat-row">
        <Stat
          label="Combined Brier"
          value={formatBrier(summary.brier)}
          hint={graded ? `base-rate baseline ${formatBrier(summary.baselineBrier)}` : null}
        />
        <Stat
          label="Predictions"
          value={summary.total}
          hint={`${setsWithGrades} graded set${setsWithGrades === 1 ? '' : 's'}`}
        />
        <Stat
          label="Graded"
          value={summary.scored}
          hint={[
            summary.unresolved ? `${summary.unresolved} pending` : null,
            summary.voided ? `${summary.voided} voided` : null
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {/* Signed, so the tile keeps one label: positive is overconfident, negative is under. */}
        <Stat
          label="Overconfidence"
          value={gap === null ? '—' : `${gap < 0 ? '−' : '+'}${Math.abs(gap * 100).toFixed(1)} pts`}
          hint={
            gap === null
              ? null
              : `${formatPercent(summary.meanConfidence)} sure · right ${formatPercent(summary.accuracy)}`
          }
        />
      </div>

      {graded ? (
        <div className="pred-charts">
          <CalibrationChart
            buckets={calibration}
            title="Calibration, all sets pooled"
            note="Dot size = predictions in that bucket across every set. Forecasts below 0.5 are counted as their complement."
          />
          <BrierOverTimeChart points={points} />
          <ConfidenceChart points={points} />
          <CategoryBrierChart rows={categoryRows} />
        </div>
      ) : (
        <p className="pred-empty">
          Nothing in this selection has been graded yet, so there's no score to combine.
        </p>
      )}
    </section>
  );
}
